'use client'

import { useIsPunchout } from '@/lib/auth/client/AuthProvider.client'
import { createCartUpdatedIntent } from '@/feature/cart/services/cart.intents'
import type { MergedProduct } from '@/feature/product/types/mergedProduct'
import type { ProductRowReadOnlyItem } from '@/feature/product/components/product-row-read-only/ProductRowReadOnly.client'
import { usePunchoutCartId } from '@/feature/punchout/hooks/usePunchoutCartId'
import { handleErrors } from '@/lib/graphql/apiErrors'
import { isItemDefined } from '@/lib/utils/array'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { useToast } from '@/ui/toast/Toast'
import { useMutation } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  AddProductToCartDocument,
  AddProductToPunchoutCartFromProductDocument,
  ErrorCode,
} from '@gql-generated/graphql'

gql(/* GraphQL */ `
  mutation AddProductToCart($reference: String!, $quantity: Int!, $cartId: String!) {
    addProductToCart(reference: $reference, quantity: $quantity, cartId: $cartId) {
      cart {
        id
        hasClickAndCollectOnlyItems
        sumItemsQuantity
        availableClickAndCollectQuantity
        items {
          ...ProductRowReadOnly_CartItem
        }
        ...MiniCart_Cart
        ...MiniCartButton_Cart
      }
      shipping {
        fee {
          ...ShippingFeeTag_Fee
        }
      }
    }
  }

  mutation AddProductToPunchoutCartFromProduct(
    $punchoutCartId: Int!
    $reference: String!
    $quantity: Int!
  ) {
    addProductToPunchoutCart(
      punchoutCartId: $punchoutCartId
      reference: $reference
      quantity: $quantity
    ) {
      id
      totalQuantity
      totalExclTax
      items {
        id
        quantity
        rowTotalExclTax
        conditioningUnitPriceExclTax
        catalogDiscountAmount
        product {
          reference
          ...ProductRowReadOnly_Product
        }
      }
    }
  }
`)

export type AddToCartOutcome = {
  cartId: string
  addedItems: ProductRowReadOnlyItem[]
  hasClickAndCollectOnlyItems: boolean
}

type AddToCartVariables = {
  product?: MergedProduct
  reference: string
  quantity: number
  cartId?: string
}

type AddToCartOptions = {
  variables?: AddToCartVariables
  onCompleted?: (outcome: AddToCartOutcome) => void
  // Called in addition to (not instead of) the error toast already handled below
  onError?: () => void
}

type AddToCartResult = {
  loading: boolean
}

export function useAddProductToCart(
  defaultOptions?: AddToCartOptions,
): [(options?: AddToCartOptions) => Promise<void>, AddToCartResult] {
  const toast = useToast()
  const { t } = useTranslation(['productBuyBox', 'checkoutCart'])
  const isPunchout = useIsPunchout()
  const { resolvePunchoutCartId } = usePunchoutCartId()

  const [mutate, { loading }] = useMutation(AddProductToCartDocument)
  const [mutatePunchout, { loading: isAddingToPunchoutCart }] = useMutation(
    AddProductToPunchoutCartFromProductDocument,
  )

  const notifyError = () =>
    toast.error({
      dedupe: false,
      title: t('checkoutCart:productReference.error.title'),
      message: t('checkoutCart:addToCart.errors.default'),
    })

  const addToCart = async (options?: AddToCartOptions) => {
    const variables = options?.variables ?? defaultOptions?.variables
    const onCompleted = options?.onCompleted ?? defaultOptions?.onCompleted
    const onError = options?.onError ?? defaultOptions?.onError

    if (!variables) {
      throw new Error('useAddProductToCart: variables are required')
    }

    const { reference, quantity, cartId } = variables

    if (isPunchout) {
      const punchoutCartId = await resolvePunchoutCartId()

      if (!punchoutCartId) {
        onError?.()
        notifyError()

        return
      }

      await mutatePunchout({
        variables: { punchoutCartId, reference, quantity },
        onCompleted: (data) => {
          const cart = data.addProductToPunchoutCart

          onCompleted?.({
            cartId: String(cart.id),
            addedItems: (cart.items ?? [])
              .filter(isItemDefined)
              .filter((item) => item.product.reference === reference)
              .map((item) => ({
                quantity: item.quantity,
                totalExclTax: item.rowTotalExclTax,
                unitPrice: { exclTax: item.conditioningUnitPriceExclTax },
                discount: { catalogDiscountAmount: item.catalogDiscountAmount },
                product: item.product,
              })),
            hasClickAndCollectOnlyItems: false,
          })

          handleIntent(createCartUpdatedIntent())
        },
        onError: () => {
          onError?.()
          notifyError()
        },
      })

      return
    }

    if (!cartId) {
      onError?.()
      notifyError()

      return
    }

    await mutate({
      variables: { reference, quantity, cartId },
      onCompleted: (data) => {
        const cart = data.addProductToCart.cart

        onCompleted?.({
          cartId: cart.id,
          addedItems: (cart.items ?? [])
            .filter(isItemDefined)
            .filter((item) => item.product?.reference === reference),
          hasClickAndCollectOnlyItems: cart.hasClickAndCollectOnlyItems,
        })

        handleIntent(createCartUpdatedIntent())
      },
      onError: (error) => {
        onError?.()

        handleErrors(error.graphQLErrors, {
          onError: {
            [ErrorCode.AddSimpleProductsToCartMaxQuantityReachedError]: () =>
              toast.error({
                dedupe: false,
                title: t('checkoutCart:productReference.error.title'),
                message: t(
                  'checkoutCart:addToCart.errors.AddSimpleProductsToCartMaxQuantityReachedError',
                ),
              }),
            [ErrorCode.AddSimpleProductsToCartGenericError]: () =>
              toast.error({
                dedupe: false,
                title: t('checkoutCart:productReference.error.title'),
                message: t('checkoutCart:addToCart.errors.AddSimpleProductsToCartGenericError'),
              }),
            default: notifyError,
          },
        })
      },
    })
  }

  return [addToCart, { loading: loading || isAddingToPunchoutCart }]
}
